import queryString from 'query-string';
import set from 'lodash/set';
import get from 'lodash/get';
import { captureTaggedException } from '../utilities/analytics/error-reporting';
import { State } from '../reducers';

import {
  Action,
  CheckUserNamePasswordlessType,
  CheckUserNameRedirectType,
  CheckUserNameSocialType,
  Dispatch,
  JWT,
  LoginFieldError,
  LoginGlobalError,
  LoginState,
  ThunkAction,
  URLParameters,
  UserFlow,
  IsolatedCloudCheckUsernameResponse,
} from '../types';
import { buildMarketingConsent, ConsentCheckboxState } from '../utilities/marketing-consent';
import { AnalyticsClient } from '../utilities/analytics/analytics-web-client';
import { loginPageId } from '../pages/login/constants';
import { MarketingConsentConfig } from '../reducers/marketing-consent-reducer';
import { login } from '../utilities/login/atlassian-authentication';
import { updateCsrfToken } from './csrf-token-actions';
import { ChallengeResponse } from '../contexts/recaptcha';
import { toChallengeRequest } from '../utilities/recaptcha';
import { LoginPageState } from '../utilities/hooks/useLoginState';

interface Identities {
  google?: boolean;
  microsoft?: boolean;
  apple?: boolean;
  slack?: boolean;
  password: boolean;
}

export interface RedirectUserAction {
  action: 'redirect';
  redirect_uri: string;
  redirect_type: CheckUserNameRedirectType;
}

export interface IsolatedCloudRedirectUserAction {
  action: 'ic_redirect';
  ic_id: string;
  ic_display_name?: string;
  ic_label?: string;
  redirect_uri: string;
}

export type IsolatedCloudCheckUsernameContinueRequiredAction = {
  action: 'continue_required';
};

export interface SignupUserAction {
  action: 'signup';
}

export interface NoUserAction {
  action: 'no_action';
  passwordless?: CheckUserNamePasswordlessType;
  social?: CheckUserNameSocialType;
}

export interface SocialLoginAction {
  action: 'social_login';
  passwordless?: CheckUserNamePasswordlessType;
  identities: Identities;
}

export type UserAction =
  | RedirectUserAction
  | IsolatedCloudCheckUsernameContinueRequiredAction
  | SignupUserAction
  | NoUserAction
  | SocialLoginAction
  | IsolatedCloudRedirectUserAction;

export const rejectNonOkResponse = (response: Response): Response | Promise<any> =>
  response.ok
    ? response
    : response.text().then(error => {
        throw new Error(error || response.statusText);
      });

export const requestCheckUsername = (): Action => ({
  type: 'LOGIN_REQUEST_CHECK_USERNAME',
});

export const requestCheckUsernameRedirect = (userAction: RedirectUserAction): Action => ({
  type: 'LOGIN_REQUEST_CHECK_USERNAME_REDIRECT',
  redirectUrl: userAction.redirect_uri,
  redirectType: userAction.redirect_type,
});

export const requestIsolatedCloudRedirect = (
  userAction: IsolatedCloudRedirectUserAction
): Action => ({
  type: 'LOGIN_REQUEST_CHECK_USERNAME_ISOLATED_CLOUD_REDIRECT',
  redirect_uri: userAction.redirect_uri,
});

export const requestIsolatedCloudCheckUsername = (): Action => ({
  type: 'LOGIN_REQUEST_ISOLATED_CLOUD_CHECK_USERNAME',
});

export const requestIsolatedCloudCheckUsernameSucceeded = (
  accounts: IsolatedCloudCheckUsernameResponse
): Action => ({
  type: 'LOGIN_REQUEST_ISOLATED_CLOUD_CHECK_USERNAME_SUCCEEDED',
  accounts,
});

export const requestCheckUsernameSignup = (): Action => ({
  type: 'LOGIN_REQUEST_CHECK_USERNAME_SIGNUP',
});

export const requestCheckUsernameNoAction = (userAction: NoUserAction): Action => ({
  type: 'LOGIN_REQUEST_CHECK_USERNAME_NO_ACTION',
  passwordless: userAction.passwordless || CheckUserNamePasswordlessType.Disallowed,
  social: userAction.social || 'allowed',
});

export const requestCheckUsernameSocialLogin = (userAction: SocialLoginAction): Action => ({
  type: 'LOGIN_REQUEST_CHECK_USERNAME_SOCIAL_LOGIN',
  passwordless: userAction.passwordless || CheckUserNamePasswordlessType.Disallowed,
  identities: userAction.identities,
});

export const requestCheckUsernameSucceeded =
  (usernameCheckParams: UsernameCheckParams, userAction: UserAction): ThunkAction =>
  dispatch => {
    switch (userAction.action) {
      case 'redirect':
        dispatch(requestCheckUsernameRedirect(userAction));
        break;
      case 'signup':
        dispatch(requestCheckUsernameSignup());
        break;
      case 'no_action':
        dispatch(requestCheckUsernameNoAction(userAction));
        break;
      case 'social_login':
        if (!userAction.identities || userAction.identities.password === undefined) {
          // this event is needed for growth team's experiment slo
          // we want to silently fire this event and not show any error
          // this will just not enrol user in the experiment
          usernameCheckParams.analyticsClient.operationalEvent({
            page: loginPageId,
            action: 'loadSocialLoginPage',
            subject: 'sli',
            attributes: {
              hasError: true,
              errorName: 'identities not as expected',
            },
          });
        }
        dispatch(requestCheckUsernameSocialLogin(userAction));
        break;
      case 'continue_required':
        dispatch(isolatedCloudCheckUsernameContinueRequired(usernameCheckParams));
        break;
      case 'ic_redirect':
        dispatch(requestIsolatedCloudRedirect(userAction));
        break;
    }
    return Promise.resolve();
  };

export const checkUsernameCaptchaFailure = (isOnline: boolean) =>
  requestCheckUsernameError({ code: isOnline ? 'captcha' : 'request_error' });

export const requestCheckUsernameFailed = (error: LoginFieldError['email']): Action => ({
  type: 'LOGIN_REQUEST_CHECK_USERNAME_FAILED',
  error,
});

export const requestCheckUsernameError = (error: LoginGlobalError): Action => ({
  type: 'LOGIN_REQUEST_CHECK_USERNAME_ERROR',
  error,
});

export const resetLoginForm = (): Action => ({
  type: 'LOGIN_RESET',
});

export const resetRememberMeState = (): Action => ({
  type: 'RESET_REMEMBER_ME_STATE',
});

export const requestUsernamePasswordLogin = (): Action => ({
  type: 'LOGIN_REQUEST_USERNAME_PASSWORD_LOGIN',
});

export const requestPasswordlessLogin = (): Action => ({
  type: 'LOGIN_REQUEST_PASSWORDLESS_LOGIN',
});

export const requestPasswordlessSucceeded = (): Action => ({
  type: 'LOGIN_REQUEST_PASSWORDLESS_LOGIN_SUCCESS',
});

export const requestPasswordlessFailed = (error: LoginGlobalError): Action => ({
  type: 'LOGIN_REQUEST_PASSWORDLESS_LOGIN_FAILED',
  error,
});

export const requestUsernamePasswordLoginSucceeded = (redirectUrl = ''): Action => ({
  type: 'LOGIN_REQUEST_USERNAME_PASSWORD_LOGIN_SUCCESS',
  redirectUrl,
});

export const requestUsernamePasswordLoginFailed = (error: LoginGlobalError): Action => ({
  type: 'LOGIN_REQUEST_USERNAME_PASSWORD_LOGIN_FAILED',
  error: error,
});

export const redirectToUserInactivePage = (redirectUrl: string): Action => ({
  type: 'LOGIN_REDIRECT_USER',
  redirectUrl,
});

export const updateLoginAttemptDetails = (nextAttempt: number): Action => ({
  type: 'UPDATE_LOGIN_ATTEMPT_DETAILS',
  nextAttempt,
});

export const updateCaptchaRequirement = (isCaptchaRequired: boolean): Action => ({
  type: 'LOGIN_UPDATE_CAPTCHA_REQUIREMENT',
  isCaptchaRequired,
});

export const showFormErrors = (errors: LoginFieldError): Action => ({
  type: 'LOGIN_SHOW_LOGIN_FORM_ERRORS',
  errors,
});

export const setRememberMeCheckbox = (checked: boolean): Action => ({
  type: 'REMEMBER_ME_CHECKBOX_UPDATE',
  payload: {
    enabled: checked,
  },
});

export const toggleRememberMeCheckbox = (): Action => ({
  type: 'REMEMBER_ME_CHECKBOX_TOGGLE',
});

export const loginSetState = (state: LoginPageState): Action => ({
  type: 'LOGIN_SET_STATE',
  state,
});

export type UsernameCheckParams = {
  username: string;
  params: string;
  analyticsClient: AnalyticsClient;
  recoveryToken?: JWT;
  challengeResponse?: ChallengeResponse | null;
};

const usernameCheck =
  ({
    username,
    params,
    analyticsClient,
    recoveryToken,
    challengeResponse,
  }: UsernameCheckParams): ThunkAction =>
  async (dispatch, getState) => {
    dispatch(requestCheckUsername());

    let response: Response;
    try {
      response = await fetch(`/rest/check-username${params}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': getState().csrfToken,
        },
        body: JSON.stringify({
          username,
          recoveryToken,
          'challenge-response': toChallengeRequest(challengeResponse),
        }),
      });
    } catch (networkError) {
      analyticsClient.operationalEvent({
        page: loginPageId,
        action: 'checkUsernameFailure',
        subject: 'sli',
      });
      dispatch(requestCheckUsernameError({ code: 'request_error' }));
      return;
    }

    let analyticsAction: 'checkUsernameSuccessful' | 'checkUsernameFailure';
    if (response.ok) {
      try {
        dispatch(
          requestCheckUsernameSucceeded(
            { username, params, analyticsClient, recoveryToken, challengeResponse },
            await response.json()
          )
        );
        analyticsAction = 'checkUsernameSuccessful';
      } catch (parseError) {
        dispatch(requestCheckUsernameError({ code: 'unknown' }));
        analyticsAction = 'checkUsernameFailure';
        captureTaggedException(parseError, {
          capabilities: [
            'capability-group--login',
            'capability-subgroup--username-password-login',
            'capability-name--login-with-username-password',
          ],
          extraProperties: {
            description: 'Check-username response OK but failed to parse it',
          },
        });
      }
    } else {
      analyticsAction = 'checkUsernameFailure';
      let responseErrorText: string | null = null;
      try {
        if (response.status === 429) {
          dispatch(requestCheckUsernameError({ code: 'too_many_username_requests' }));
        } else if (response.status === 400) {
          const { uiErrors } = await response.json();
          if (uiErrors && uiErrors.length) {
            dispatch(requestCheckUsernameError({ code: uiErrors[0].key || 'unknown' }));
          }
        } else {
          responseErrorText = (await response.text()) || response.statusText;
          const extractedError: LoginFieldError['email'] =
            JSON.parse(responseErrorText).formErrors[0];
          dispatch(requestCheckUsernameFailed(extractedError));
        }
      } catch (parseError) {
        captureTaggedException(parseError, {
          capabilities: [
            'capability-group--login',
            'capability-subgroup--username-password-login',
            'capability-name--login-with-username-password',
          ],
          extraProperties: {
            description: 'Failed to handle non-OK response from check-username',
            responseStatus: response.status,
            responseErrorText,
          },
        });
        dispatch(requestCheckUsernameError({ code: 'unknown' }));
      }
    }

    analyticsClient.operationalEvent({
      page: loginPageId,
      action: analyticsAction,
      subject: 'sli',
    });
  };

export const checkUsername =
  ({
    username,
    params,
    analyticsClient,
    recoveryToken,
    challengeResponse,
  }: UsernameCheckParams): ThunkAction =>
  dispatch => {
    analyticsClient.operationalEvent({
      page: loginPageId,
      action: 'checkUsernameStart',
      subject: 'sli',
    });

    return dispatch(
      usernameCheck({
        username,
        params,
        analyticsClient,
        recoveryToken,
        challengeResponse,
      })
    );
  };

export const isolatedCloudCheckUsername =
  ({
    username,
    params,
    analyticsClient,
    recoveryToken,
    challengeResponse,
  }: UsernameCheckParams): ThunkAction =>
  async (dispatch, getState) => {
    dispatch(requestIsolatedCloudCheckUsername());

    analyticsClient.operationalEvent({
      page: loginPageId,
      action: 'checkIsolatedCloudUsernameStart',
      subject: 'sli',
    });

    const csrfToken = getState().csrfToken;

    let response: Response;
    try {
      response = await fetch(`/rest/v2/check-username${params}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken,
        },
        body: JSON.stringify({
          username,
          recoveryToken,
          'challenge-response': toChallengeRequest(challengeResponse),
        }),
      });
    } catch (networkError) {
      dispatch(requestCheckUsernameError({ code: 'request_error' }));
      return;
    }

    let analyticsAction:
      | 'checkIsolatedCloudUsernameSuccessful'
      | 'checkIsolatedCloudUsernameFailure';

    if (response.ok) {
      try {
        const accounts = (await response.json()) as IsolatedCloudCheckUsernameResponse;
        dispatch(requestIsolatedCloudCheckUsernameSucceeded(accounts));
        analyticsAction = 'checkIsolatedCloudUsernameSuccessful';
      } catch (parseError) {
        dispatch(requestCheckUsernameError({ code: 'unknown' }));
        analyticsAction = 'checkIsolatedCloudUsernameFailure';
        captureTaggedException(parseError, {
          capabilities: [
            'capability-group--login',
            'capability-subgroup--username-password-login',
            'capability-name--login-with-username-password',
          ],
          extraProperties: {
            description: 'Check-username Isolated Cloud response OK but failed to parse it',
          },
        });
      }
    } else {
      analyticsAction = 'checkIsolatedCloudUsernameFailure';
      let responseErrorText: string | null = null;
      try {
        if (response.status === 429) {
          dispatch(requestCheckUsernameError({ code: 'too_many_username_requests' }));
        } else if (response.status === 400) {
          const { uiErrors } = await response.json();
          if (uiErrors && uiErrors.length) {
            dispatch(requestCheckUsernameError({ code: uiErrors[0].key || 'unknown' }));
          }
        } else {
          responseErrorText = (await response.text()) || response.statusText;
          const extractedError: LoginFieldError['email'] =
            JSON.parse(responseErrorText).formErrors[0];
          dispatch(requestCheckUsernameFailed(extractedError));
        }
      } catch (parseError) {
        captureTaggedException(parseError, {
          capabilities: [
            'capability-group--login',
            'capability-subgroup--username-password-login',
            'capability-name--login-with-username-password',
          ],
          extraProperties: {
            description: 'Failed to handle non-OK response from check-username Isolated Cloud',
            responseStatus: response.status,
            responseErrorText,
          },
        });
        dispatch(requestCheckUsernameError({ code: 'unknown' }));
      }
    }

    analyticsClient.operationalEvent({
      page: loginPageId,
      action: analyticsAction,
      subject: 'sli',
    });
  };

export const isolatedCloudCheckUsernameContinueRequired =
  ({
    username,
    params,
    analyticsClient,
    recoveryToken,
    challengeResponse,
  }: UsernameCheckParams): ThunkAction =>
  async (dispatch, getState) => {
    const csrfToken = getState().csrfToken;

    analyticsClient.operationalEvent({
      page: loginPageId,
      action: 'checkIsolatedCloudUsernameContinueRequiredStart',
      subject: 'sli',
    });

    let analyticsAction:
      | 'checkIsolatedCloudUsernameContinueRequiredSuccessful'
      | 'checkIsolatedCloudUsernameContinueRequiredFailure';
    let response: Response;
    try {
      response = await fetch(`/rest/v2/check-username/continue${params}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken,
        },
        body: JSON.stringify({
          username,
          recoveryToken,
          'challenge-response': toChallengeRequest(challengeResponse),
        }),
      });
    } catch (networkError) {
      analyticsClient.operationalEvent({
        page: loginPageId,
        action: 'checkIsolatedCloudUsernameContinueRequiredFailure',
        subject: 'sli',
      });
      dispatch(requestCheckUsernameError({ code: 'request_error' }));
      return;
    }

    if (response.ok) {
      const userAction = (await response.json()) as RedirectUserAction;
      dispatch(requestCheckUsernameRedirect(userAction));
      analyticsAction = 'checkIsolatedCloudUsernameContinueRequiredSuccessful';
    } else {
      let responseErrorText: string | null = null;
      try {
        if (response.status === 429) {
          dispatch(requestCheckUsernameError({ code: 'too_many_username_requests' }));
        } else if (response.status === 400) {
          const { uiErrors } = await response.json();
          if (uiErrors && uiErrors.length) {
            dispatch(requestCheckUsernameError({ code: uiErrors[0].key || 'unknown' }));
          }
        } else {
          responseErrorText = (await response.text()) || response.statusText;
          const extractedError: LoginFieldError['email'] =
            JSON.parse(responseErrorText).formErrors[0];
          dispatch(requestCheckUsernameFailed(extractedError));
        }
      } catch (parseError) {
        captureTaggedException(parseError, {
          capabilities: [
            'capability-group--login',
            'capability-subgroup--username-password-login',
            'capability-name--login-with-username-password',
          ],
          extraProperties: {
            description: 'Failed to handle non-OK response from check-username Isolated Cloud',
            responseStatus: response.status,
            responseErrorText,
          },
        });
      }
      analyticsAction = 'checkIsolatedCloudUsernameContinueRequiredFailure';
    }

    analyticsClient.operationalEvent({
      page: loginPageId,
      action: analyticsAction,
      subject: 'sli',
    });
  };

function reportFrontendError(error: Object & { error_description?: string }) {
  function removeField(obj: Object, fieldName: string) {
    for (const prop in obj) {
      if (prop === fieldName) {
        delete obj[prop];
      } else if (typeof obj[prop] === 'object') {
        removeField(obj[prop], fieldName);
      }
    }
  }

  function decodeJsonField(obj: Object, path: string) {
    const val = get(obj, path);
    if (val !== 'undefined') {
      try {
        set(obj, path, JSON.parse(val));
      } catch (_) {}
    }
  }

  removeField(error, 'password');
  decodeJsonField(error, 'original.response.text');
  const errorCode = error['error'];
  captureTaggedException(error, {
    capabilities: [
      'capability-group--login',
      'capability-subgroup--username-password-login',
      'capability-name--login-with-username-password',
    ],
    extraProperties: {
      description: error?.error_description || 'Failed to login for an unexpected reason',
      code: errorCode,
    },
  });
}

function parseAdditionalData(err: any) {
  try {
    return JSON.parse(atob(err.error_description));
  } catch (e) {
    return {};
  }
}

function handleLoginError(
  err: Object,
  username: string,
  application: string | undefined,
  dispatch: Dispatch,
  getState: () => State,
  onLoginError: (string: string) => void
) {
  const additionalData = parseAdditionalData(err);
  const code = (additionalData['custom_error'] || err['error'] || '').toLowerCase();
  const isBitbucket = application && application.toLowerCase() === 'bitbucket';
  if (code === 'too_many_attempts' || code === 'invalid_captcha') {
    dispatch(updateCaptchaRequirement(true));
    dispatch(requestUsernamePasswordLoginFailed({ code: 'captcha' }));
  } else if (code === 'access_denied') {
    dispatch(updateLoginAttemptDetails(getState().login.nextAttempt + 1));
    dispatch(requestUsernamePasswordLoginFailed({ code: 'invalid_user_password' }));
  } else if (code === 'ambiguous_user') {
    dispatch(requestUsernamePasswordLoginFailed({ code: 'ambiguous_user' }));
  } else if (code === 'no_email_on_remote_user') {
    dispatch(
      requestUsernamePasswordLoginFailed({
        code: 'no_email_on_remote_user',
        args: { username: username },
      })
    );
  } else if (code === 'username_is_not_email') {
    const errorCode = isBitbucket ? 'bitbucket_username_is_not_email' : 'username_is_not_email';
    dispatch(
      requestUsernamePasswordLoginFailed({
        code: errorCode,
        args: { username: additionalData['email'] },
      })
    );
  } else if (code === 'email_is_not_primary') {
    const errorCode = isBitbucket ? 'bitbucket_email_is_not_primary' : 'email_is_not_primary';
    dispatch(
      requestUsernamePasswordLoginFailed({
        code: errorCode,
        args: { username: additionalData['email'] },
      })
    );
  } else if (code === 'too_many_logins') {
    dispatch(requestUsernamePasswordLoginFailed({ code: 'too_many_logins' }));
  } else if (code === 'password_leaked') {
    dispatch(requestUsernamePasswordLoginFailed({ code: 'password_leaked' }));
  } else if (code === 'blocked_user') {
    const redirectURL = application
      ? `/login/inactive?application=${application}`
      : '/login/inactive';
    dispatch(redirectToUserInactivePage(redirectURL));
  } else if (code === 'request_error') {
    // 'request_error' auth0-js: connection fails due to offline or cross domain issue
    dispatch(requestUsernamePasswordLoginFailed({ code: 'request_error' }));
  } else if (code === 'invalid.session.token') {
    dispatch(requestUsernamePasswordLoginFailed({ code: 'invalid.session.token' }));
  } else {
    reportFrontendError(err);
    dispatch(requestUsernamePasswordLoginFailed({ code: 'unknown' }));
  }

  if (code !== 'too_many_attempts' && code !== 'invalid_captcha') {
    dispatch(updateCaptchaRequirement(false));
  }

  onLoginError(code);
}

function verifyCaptchaIfRequired(captchaValue: string | null | undefined, username: string) {
  if (!captchaValue) {
    return Promise.resolve();
  }

  return fetch('/rest/captcha/verify', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      'g-recaptcha-response': captchaValue,
      email: username,
    }),
  })
    .then(rejectNonOkResponse)
    .catch(() => {
      const error = new Error('invalid_captcha');

      return Promise.reject(error);
    });
}

export const prepareState = (
  csrfToken: string,
  config: MarketingConsentConfig | null | undefined,
  marketingConsentState: ConsentCheckboxState,
  anonymousId?: string,
  userFlow?: UserFlow
): LoginState => {
  let state: LoginState = { csrfToken, anonymousId, userFlow };
  const marketingConsent = buildMarketingConsent(config, marketingConsentState);
  if (marketingConsent) {
    state = { ...state, marketingConsent };
  }
  return state;
};

interface LoginParams {
  username: string;
  password: string;
  captchaValue: string | null | undefined;
  location: Location;
  onLoginError: (code: string) => void;
  marketingConsent: ConsentCheckboxState;
  anonymousId: string;
}

export const loginUsernamePassword =
  (loginParams: LoginParams, navigate: (url: string) => void): ThunkAction =>
  (dispatch, getState) => {
    const { location, username, password, captchaValue, onLoginError, anonymousId } = loginParams;
    const params: URLParameters = queryString.parse(location.search);
    const { application, userFlow } = params;
    const state = getState();
    const { isRememberMeChecked } = state.login;
    const { csrfToken, marketingConsent } = state;

    dispatch(requestUsernamePasswordLogin());
    const loginState = prepareState(
      csrfToken,
      marketingConsent.config,
      loginParams.marketingConsent,
      anonymousId,
      userFlow
    );

    // This passes the `remember` flag to the backend, "remembering" the account that we're logging into.
    if (isRememberMeChecked) {
      params.remember = 'true';
    }

    const query = queryString.stringify(params);

    const loginWithAtlassianAuth = (navigate: (url: string) => void) =>
      login({
        username,
        password,
        state: loginState,
        query: `?${query}${location.hash}`,
      }).then(result => {
        switch (result.kind) {
          case 'failure': {
            const err = { error: result.reason };
            return handleLoginError(err, username, application, dispatch, getState, onLoginError);
          }
          case 'success':
            return navigate(result.redirectUri);
        }
      });

    return verifyCaptchaIfRequired(captchaValue, username)
      .then(() => loginWithAtlassianAuth(navigate))
      .catch(err => {
        captureTaggedException(err, {
          capabilities: [
            'capability-group--login',
            'capability-subgroup--username-password-login',
            'capability-name--login-with-username-password',
          ],
          extraProperties: {
            description: 'Failed to login',
          },
        });
        handleLoginError(err, username, application, dispatch, getState, onLoginError);
      });
  };

interface PasswordlessLoginParams {
  email: string;
  captchaValue: string | null | undefined;
  location: Location;
}

export const loginPasswordless =
  ({
    email,
    captchaValue,
    location,
  }: PasswordlessLoginParams): ThunkAction<Promise<{ success: boolean }>> =>
  async (dispatch, getState) => {
    const {
      csrfToken,
      login: { isRememberMeChecked },
    } = getState();

    dispatch(requestPasswordlessLogin());

    const urlParams = new URLSearchParams(location.search);

    if (isRememberMeChecked) {
      urlParams.set('remember', 'true');
    }

    const apiParams = urlParams.toString();

    try {
      const response = await fetch(`/rest/login/passwordless?${apiParams}`, {
        method: 'POST',
        headers: {
          'X-CSRF-TOKEN': csrfToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, captchaValue }),
      });

      const { csrfToken: newCsrfToken } = await response.json();
      if (newCsrfToken) {
        dispatch(updateCsrfToken(newCsrfToken));
      }

      if (response.ok) {
        dispatch(requestPasswordlessSucceeded());
        return { success: true };
      } else {
        dispatch(requestPasswordlessFailed({ code: 'unknown' }));
        return { success: false };
      }
    } catch (error) {
      captureTaggedException(error, {
        capabilities: [
          'capability-group--login',
          'capability-subgroup--passwordless-login',
          'capability-name--passwordless-login',
        ],
        extraProperties: {
          description: 'Failed passwordless login',
        },
      });
      dispatch(requestPasswordlessFailed({ code: 'request_error' }));
      return { success: false };
    }
  };
